import { useEffect, useRef } from 'react';

// Keyframes injected once at module load; CSS lives here rather than in the global
// stylesheet so it's co-located with the only consumer.
const SPARK_CSS = `
  @keyframes ah-sparkfly {
    0%   { opacity: 0; transform: translate(-50%, -50%) scale(.2) rotate(0deg); }
    25%  { opacity: 1; }
    100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(var(--ds)) rotate(var(--dr)); }
  }
  .ah-spark {
    position: fixed;
    width: 12px;
    height: 12px;
    pointer-events: none;
    z-index: 9999;
    animation: ah-sparkfly 0.62s ease forwards;
  }
`;

let sparkStyleInjected = false;
const ensureSparkStyle = () => {
  if (sparkStyleInjected) return;
  const style = document.createElement('style');
  style.textContent = SPARK_CSS;
  document.head.appendChild(style);
  sparkStyleInjected = true;
};

// Read at module load — static, but this is acceptable: the preference rarely changes
// mid-session and the OS re-evaluating it per-interaction would add no UX benefit.
const reduceMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SPARK_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" style="width:100%;height:100%;display:block"><path d="M12 0c.6 5.4 2.4 9 12 12-9.6 3-11.4 6.6-12 12-.6-5.4-2.4-9-12-12 9.6-3 11.4-6.6 12-12z"/></svg>';

const BURST_DURATION_MS = 680;

/**
 * Returns a `burst(el, color)` callback that fires a sparkle animation from the
 * centre of `el`. All pending timers and DOM nodes are cleaned up on unmount,
 * preventing leaks if the component unmounts mid-animation.
 */
export const useSparkleBurst = () => {
  // Track every pending timer so we can cancel on unmount.
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    },
    []
  );

  const burst = (el: HTMLElement, color: string, count = 8) => {
    if (reduceMotion) return;
    ensureSparkStyle();

    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;

    for (let i = 0; i < count; i++) {
      const spark = document.createElement('div');
      spark.className = 'ah-spark';
      const ang = Math.random() * Math.PI * 2;
      const dist = 24 + Math.random() * 38;
      spark.style.left = `${cx}px`;
      spark.style.top = `${cy}px`;
      spark.style.color = color;
      spark.style.setProperty('--dx', `${Math.cos(ang) * dist}px`);
      spark.style.setProperty('--dy', `${Math.sin(ang) * dist}px`);
      spark.style.setProperty('--ds', (0.6 + Math.random() * 0.8).toFixed(2));
      spark.style.setProperty('--dr', `${Math.random() * 180 - 90}deg`);
      spark.innerHTML = SPARK_SVG;
      document.body.appendChild(spark);

      const timer = setTimeout(() => {
        spark.remove();
        timersRef.current = timersRef.current.filter((t) => t !== timer);
      }, BURST_DURATION_MS);

      timersRef.current.push(timer);
    }
  };

  return { burst, reduceMotion };
};
